'use client';

import { EmblaOptionsType } from 'embla-carousel';
import { useQuery } from '@tanstack/react-query';

import { fetchCombo, type TComboResponse } from '@/actions/fetch-combo';
import { SafariCountriesSection } from '@/components/safari-countries/SafariCountriesSection';
import { TourCarousel } from '@/components/search/carousel';
import type { PaymentCurrency } from '@/config/currency';
import { useCurrency } from '@/lib/context/CurrencyContext';
import { QUERY_KEYS } from '@/lib/query-keys';

const OPTIONS: EmblaOptionsType = { loop: true, align: 'start' };

type HomeCarouselsClientProps = {
  initialCombo: TComboResponse;
  /** SSR cookie currency — used only to seed the matching query key. */
  initialCurrency: PaymentCurrency;
};

export function HomeCarouselsClient({ initialCombo, initialCurrency }: HomeCarouselsClientProps) {
  const { currentCurrency } = useCurrency();

  const { data, isPending } = useQuery({
    queryKey: QUERY_KEYS.combo(currentCurrency),
    queryFn: () => fetchCombo(currentCurrency),
    // Seed SSR data only for the cookie currency; never seed EUR with USD props.
    initialData: currentCurrency === initialCurrency ? initialCombo : undefined,
    // Avoid remount SA refetch on navigation while RSC still provides fresh seed data.
    staleTime: 5 * 60 * 1000,
  });

  if (!data) {
    return (
      <div className="flex flex-col gap-20 lg:gap-24 -mt-12 lg:-mt-16">
        <div className="h-8 w-48 bg-gray-800 rounded animate-pulse" />
        <div className="h-64 bg-gray-800/50 rounded-lg animate-pulse" />
        <SafariCountriesSection />
        <div className="h-8 w-48 bg-gray-800 rounded animate-pulse" />
        <div className="h-64 bg-gray-800/50 rounded-lg animate-pulse" />
        {isPending ? (
          <p className="text-dimmer text-sm" aria-live="polite">
            Updating prices…
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <>
      <section className="-mt-12 lg:-mt-16">
        <div className="section-title carousel-title font-medium">
          <h2 role="title" className="inline">
            Our choice.
          </h2>
          <span className="h2">
            {' '}
            <span className="text-dimmer">Here are some picks that we love</span>
          </span>
        </div>
        <TourCarousel tours={data.ourChoice} options={OPTIONS} />
      </section>

      <SafariCountriesSection />

      <section className="-mt-12 lg:-mt-16">
        <div className="section-title carousel-title font-medium">
          <h2 role="title" className="inline">
            Most popular.
          </h2>
          <span className="h2">
            {' '}
            <span className="text-dimmer">Check out what others are choosing</span>
          </span>
        </div>
        <TourCarousel tours={data.mostPopular} options={OPTIONS} />
      </section>

      <section className="-mt-12 lg:-mt-16">
        <div className="section-title carousel-title font-medium">
          <h2 role="title" className="inline">
            Freshly added.
          </h2>
          <span className="h2">
            {' '}
            <span className="text-dimmer">New tours available</span>
          </span>
        </div>
        <TourCarousel tours={data.newOnes} options={OPTIONS} />
      </section>
    </>
  );
}
